import React, { useContext, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import Link from '@atoms/Link'
import Layout from '@src/components/Layout'
import ArticleHero from '@src/components/ArticleHero'
import ArticleContent from '@src/components/ArticleContent'
import ArticleSns from '@src/components/ArticleSns'
import ArticleMask from '@src/components/ArticleMask'
import RequestInfo from '@src/components/RequestInfo'
import RelatedArticles from '@src/components/RelatedArticles'
import ArtisanArticleLayout from '@src/components/Artisan/ArtisanArticleLayout'
import SideBar from '@src/components/ArticleSideBar'
import {
  Article,
  ArticleContent as ArticleContentType,
  TextAreaContent,
} from '@models/Article'
import { removeHtmlTags } from '@models/Util'
import {
  buildTargetLocalePath,
  Locale,
  useTranslation,
} from '@models/Translation'
import { StoreContext } from '@models/Store'

import * as style from './style.module.sass'

import artisanImg from './assets/artisan.jpg'
import artisanPcImg from './assets/artisan-pc.jpg'

export type ArticleLayoutProps = {
  article: Article
  rankingArticleIds: string[]
  page?: number
  isPreview?: boolean
  isArtisan?: boolean
}

const ArticleLayout: React.FC<ArticleLayoutProps> = ({
  article,
  rankingArticleIds,
  page = 1,
  isPreview = false,
  isArtisan = false,
}) => {
  const { locale, navigate } = useTranslation()
  const { entity } = useContext(StoreContext)
  const isMasked =
    !isPreview && entity?.user?.isSignIn !== true && article.isMembersOnly

  // SSRの結果とずれるとエラーが発生するので1ページ目をデフォルトにしてHook化
  const [pageIndex, setPageIndex] = useState(0)
  const [pageLoderRef] = useInView({
    threshold: 0.1,
    onChange(inView) {
      if (!inView || isPreview || isMasked) {
        return
      }

      if (pageIndex < article.totalPage - 1) {
        window.history.replaceState(
          {},
          '',
          `${buildTargetLocalePath(locale, article.path)}?page=${pageIndex + 2}`,
        )
        setPageIndex(pageIndex + 1)
      }
    },
  })
  const contents = isPreview
    ? [
        article.contents[0],
        [
          {
            fieldId: 'textarea',
            contents:
              '<div style="color: red; text-align: center; transform: scale(1.5); padding: 24px;">ー 以降の内容はマスク対象 ー</div>',
          } as TextAreaContent,
        ],
        ...article.contents.slice(1),
      ].flat()
    : isMasked
      ? article.contents[0]
      : article.contents.slice(0, pageIndex + 1).flat()
  const requester = article.data.requester
  const pcAdsLength = 2

  useEffect(() => {
    setPageIndex(page - 1)
  }, [page])

  useEffect(() => {
    if (isPreview) return

    const adsBreakPoint = 960
    let tasks = []
    const ADS_PC_IDS = ['34617_638194', '34617_639325']
    const ADS_SP_ID = '34617_638195'
    if (window.innerWidth > adsBreakPoint) {
      tasks = ADS_PC_IDS.map((id, index) => {
        return { yads_ad_ds: id, yads_parent_element: `ad${index}` }
      })
    } else {
      tasks = [{ yads_ad_ds: ADS_SP_ID, yads_parent_element: 'ad0' }]
    }
    window.YJ_YADS = window.YJ_YADS || { tasks: [] }
    window.YJ_YADS.tasks.push(...tasks)
  }, [isPreview])

  if (contents === undefined) {
    navigate(article.path)
    return <></>
  }

  return isArtisan ? (
    <ArtisanArticleLayout
      article={article}
      rankingArticleIds={rankingArticleIds}
      contents={contents}
      requester={requester}
      pcAdsLength={pcAdsLength}
      pageLoderRef={pageLoderRef}
      isPreview={isPreview}
      locale={locale}
      isMasked={isMasked}
    />
  ) : (
    <NormalArticleLayout
      article={article}
      rankingArticleIds={rankingArticleIds}
      contents={contents}
      requester={requester}
      pcAdsLength={pcAdsLength}
      pageLoderRef={pageLoderRef}
      isPreview={isPreview}
      locale={locale}
      isMasked={isMasked}
    />
  )
}

export default ArticleLayout

export type ArticleLayoutComponentProps = {
  article: Article
  rankingArticleIds: string[]
  contents: ArticleContentType[]
  requester: string | null
  pcAdsLength: number
  pageLoderRef: (node?: Element | null) => void
  isPreview: boolean
  locale: Locale
  isMasked: boolean
}

const NormalArticleLayout: React.FC<ArticleLayoutComponentProps> = ({
  article,
  rankingArticleIds,
  contents,
  requester,
  pcAdsLength,
  pageLoderRef,
  isPreview,
  isMasked,
}) => {
  const { t } = useTranslation()

  return (
    <Layout>
      <ArticleHero article={article} isMasked={isMasked} />

      <div className={style.contentArea}>
        <div className={style.contentAreaInner}>
          <ArticleContent contents={contents} isMasked={isMasked} />
          {isMasked && <ArticleMask redirect={article.path} />}
          {typeof requester === 'string' && (
            <RequestInfo requester={requester} />
          )}
          {!isPreview && (
            <>
              <div ref={pageLoderRef} className={style.loader} />
              <div className={style.bottomContainer}>
                <ArticleSns title={article.title} />
              </div>
            </>
          )}
        </div>

        <SideBar rankingArticleIds={rankingArticleIds} />
      </div>

      {!isPreview && ( // 広告などを含め他のコンポーネントに抜き出す
        <>
          <div className={style.relateContainer}>
            <RelatedArticles article={article} />
          </div>

          <div className={style.subContents}>
            <div className={style.content}>
              <div className={style.label}>CONCEPT VIDEO</div>
              <iframe
                src="https://player.vimeo.com/video/1067952527?h=75f2ccb6ca&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                title="fashion tech news Concept Video"
                loading="lazy"
              />
              <Link to="/about">
                <div className={style.description}>
                  {removeHtmlTags(t('video.top'))}
                </div>
              </Link>
            </div>

            <div className={style.content}>
              <div className={style.label}>PICKUP CONTENTS</div>
              <Link to="/artisan">
                <div className={style.image}>
                  <picture>
                    <source media="(max-width: 600px)" srcSet={artisanImg} />
                    <img src={artisanPcImg} alt="Artisan" />
                  </picture>
                </div>
                <div className={style.description}>
                  {t('artisan.articleSidebar')}
                </div>
              </Link>
            </div>
          </div>

          <div className={style.ads}>
            {[...Array(pcAdsLength).keys()].map(index => (
              <div id={`ad${index}`} className={style.ad} key={index} />
            ))}
          </div>
        </>
      )}
    </Layout>
  )
}
